import React, { useEffect, useState, useCallback } from 'react';
import { MemberSidebarLayout } from './SidebarLayout';
import { DiscountClaimsCard } from './DiscountClaimsCard';
import { RotateCw, Sparkles, ReceiptText, PiggyBank } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ShareSavingsButton } from './ShareSavingsButton';

const formatPhp = (n) =>
  `PHP ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SavingsHero() {
  const { api } = useAuth();
  const [stats, setStats] = useState({ total: 0, total_savings: 0, last_at: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/services/my-claims?skip=0&limit=1');
      const claims = res.data?.claims || [];
      setStats({
        total: res.data?.total || 0,
        total_savings: res.data?.total_savings || 0,
        last_at: claims[0]?.created_at || null,
      });
    } catch {
      // silent — empty state handles itself
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const lastClaimLabel = stats.last_at
    ? new Date(stats.last_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div
        className="relative overflow-hidden rounded-2xl border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/15 to-transparent p-4"
        data-testid="hero-total-savings"
      >
        <div className="flex items-center gap-2 text-[#00E676] text-[11px] uppercase tracking-wider">
          <PiggyBank className="w-4 h-4" /> Total Saved
        </div>
        <div className="mt-1 font-outfit text-2xl sm:text-3xl text-white font-semibold tabular-nums">
          {loading ? '—' : formatPhp(stats.total_savings)}
        </div>
        <p className="text-[11px] text-[#A0B4CC] mt-1">
          Across all your AGA member-discount claims to date.
        </p>
        <Sparkles className="absolute top-3 right-3 w-5 h-5 text-[#00E676]/40" />
      </div>

      <div
        className="rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/15 to-transparent p-4"
        data-testid="hero-claims-count"
      >
        <div className="flex items-center gap-2 text-[#00E5FF] text-[11px] uppercase tracking-wider">
          <ReceiptText className="w-4 h-4" /> Claims Made
        </div>
        <div className="mt-1 font-outfit text-2xl sm:text-3xl text-white font-semibold tabular-nums">
          {loading ? '—' : stats.total.toLocaleString()}
        </div>
        <p className="text-[11px] text-[#A0B4CC] mt-1">
          Number of times you&apos;ve used your AGA discount.
        </p>
      </div>

      <div
        className="rounded-2xl border border-white/10 bg-white/5 p-4"
        data-testid="hero-last-claim"
      >
        <div className="flex items-center gap-2 text-[#A0B4CC] text-[11px] uppercase tracking-wider">
          <RotateCw className="w-4 h-4" /> Most Recent
        </div>
        <div className="mt-1 font-outfit text-2xl sm:text-3xl text-white font-semibold">
          {loading ? '—' : lastClaimLabel}
        </div>
        <p className="text-[11px] text-[#A0B4CC] mt-1">
          Date of your latest discount claim.
        </p>
      </div>
    </div>
  );
}

export function MyDiscountsPage() {
  const { user } = useAuth();
  return (
    <MemberSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-full overflow-x-hidden">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">My Discounts</h1>
            <p className="text-[#A0B4CC] mt-1 text-sm">
              Audit trail of member discounts you have claimed at AGA partner merchants. Remove a claim to take it off your list.
            </p>
          </div>
          <MyDiscountsShare userRefCode={user?.referral_code} />
        </div>

        <SavingsHero />

        <DiscountClaimsCard mode="member" allowDelete />
        <p className="text-[10px] text-[#5C7599]">
          Tip: tap the <RotateCw className="inline w-3 h-3 align-text-bottom text-[#00E5FF]" /> icon to instantly re-claim a discount with the same merchant at today&apos;s member price.
        </p>
      </div>
    </MemberSidebarLayout>
  );
}

/**
 * Lightweight wrapper that re-reads the savings totals so the share button
 * next to the page title can stamp the real "I saved ₱X" number.
 */
function MyDiscountsShare({ userRefCode }) {
  const { api } = useAuth();
  const [data, setData] = useState({ total: 0, total_savings: 0 });
  useEffect(() => {
    let alive = true;
    api.get('/api/services/my-claims?skip=0&limit=1')
      .then((r) => { if (alive) setData({ total: r.data?.total || 0, total_savings: r.data?.total_savings || 0 }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);
  return (
    <ShareSavingsButton
      savings={data.total_savings}
      claims={data.total}
      referralCode={userRefCode}
      testid="my-discounts-share"
    />
  );
}

export default MyDiscountsPage;
